import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  Calendar,
  CheckCircle,
  ClipboardList,
  Users,
  FileText,
  Cpu,
  Wrench,
  Package,
  DollarSign,
  List,
  FileCheck,
  Layers,
  Eye,
  Edit,
  Check,
  X,
  Filter,
  Search,
  Plus,
  Trash2,
  Save,
  ArrowLeft,
  Play,
  StopCircle,
  AlertCircle,
  Truck,
  Factory
} from "lucide-react";
import "./dashboard.css";
import BackButton from "../../components/backbutton";

export default function SupervisorDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("list-schedule");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedProductionPlan, setSelectedProductionPlan] = useState(null);
  const [showProductionModal, setShowProductionModal] = useState(false);
  const [showInhouseModal, setShowInhouseModal] = useState(false);
  const [showReceiveMaterialModal, setShowReceiveMaterialModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [processes, setProcesses] = useState([]);

  // Sample Schedule Data
  const [schedules] = useState([
    { id: "SCH-001", customer: "ABC Corp", product: "Product A", quantity: 100, date: "2024-04-10", status: "Approved" },
    { id: "SCH-002", customer: "XYZ Ltd", product: "Product B", quantity: 50, date: "2024-04-12", status: "Approved" }
  ]);

  // Sample Approved Sale Orders
  const [approvedSaleOrders, setApprovedSaleOrders] = useState([
    { id: "SO-001", orderNumber: "SO-2024-001", customer: "ABC Corp", product: "Product A", quantity: 100, amount: "₹1,50,000", date: "2024-03-15", status: "Approved" },
    { id: "SO-002", orderNumber: "SO-2024-002", customer: "XYZ Ltd", product: "Product B", quantity: 50, amount: "₹85,000", date: "2024-03-14", status: "Approved" }
  ]);

  // Sample Production Plans
  const [productionPlans, setProductionPlans] = useState([
    { id: "PP-001", product: "Product A", quantity: 100, status: "Pending", stockAvailable: 60, required: 100, subProducts: ["Sub A1", "Sub A2"] }
  ]);

  // Sample Employees
  const [employees] = useState([
    { id: "EMP-001", name: "John Doe", designation: "Machine Operator", department: "Production", status: "Active" },
    { id: "EMP-002", name: "Jane Smith", designation: "Assembler", department: "Assembly", status: "Active" }
  ]);

  // Sample BOM Data
  const [approvedBOM] = useState([
    { id: "BOM-001", product: "Product A", components: ["Component A1", "Component A2", "Component A3"] },
    { id: "BOM-002", product: "Product B", components: ["Component B1", "Component B2"] }
  ]);

  // Sample Machines
  const [machines, setMachines] = useState([
    { id: "MCH-001", name: "Cutting Machine", type: "Industrial", status: "Active" },
    { id: "MCH-002", name: "Assembly Line", type: "Automated", status: "Active" }
  ]);

  // Sample Tools
  const [tools, setTools] = useState([
    { id: "TOL-001", name: "Blade", type: "Cutting", status: "Active" },
    { id: "TOL-002", name: "Screwdriver", type: "Assembly", status: "Active" }
  ]);

  // Sample Accessories
  const [accessories, setAccessories] = useState([
    { id: "ACC-001", name: "Safety Gloves", type: "Safety", status: "Active" },
    { id: "ACC-002", name: "Goggles", type: "Safety", status: "Active" }
  ]);

  // Sample Process Costs
  const [processCosts, setProcessCosts] = useState([
    { id: "PRC-001", processName: "Cutting", cost: "₹500", machine: "Cutting Machine", labor: "₹200", total: "₹700" }
  ]);

  // Sample Process List
  const [processList] = useState([
    { id: "PROC-001", name: "Cutting Process", product: "Product A", status: "Active" },
    { id: "PROC-002", name: "Assembly Process", product: "Product A", status: "Active" }
  ]);

  // Sample Approved Root Sheets
  const [approvedRootSheets] = useState([
    { id: "RS-001", product: "Product A", processes: ["Cutting", "Assembly", "Quality Check"], status: "Approved" },
    { id: "RS-002", product: "Product B", processes: ["Molding", "Finishing"], status: "Approved" }
  ]);

  // Sample Last Process Products
  const [lastProcessProducts] = useState([
    { id: "LP-001", product: "Product A", lastProcess: "Assembly", status: "In Progress" },
    { id: "LP-002", product: "Product B", lastProcess: "Quality Check", status: "Completed" }
  ]);

  // Inhouse Production Form State
  const [inhouseForm, setInhouseForm] = useState({
    plannedProduct: "",
    employee: "",
    machine: "",
    tool: "",
    instrument: "",
    selectedAccessories: [],
    checklist: { noDamage: false, noBent: false },
    data: { weightLoss: "", accepted: "", missedQuantity: "" }
  });

  // Receive Material Form State
  const [receiveMaterialForm, setReceiveMaterialForm] = useState({
    product: "",
    date: "",
    fromTime: "",
    toTime: "",
    fromDate: "",
    toDate: ""
  });

  // Sidebar menu items
  const tabItems = [
    { id: "list-schedule", label: "List Schedule", icon: <Calendar size={18} /> },
    { id: "list-approved-sale-order", label: "List Approved Sale Order", icon: <CheckCircle size={18} /> },
    { id: "create-production-plan", label: "Create Production Plan", icon: <ClipboardList size={18} /> },
    { id: "list-employee", label: "List Employee", icon: <Users size={18} /> },
    { id: "approved-bom", label: "Approved BOM", icon: <FileText size={18} /> },
    { id: "add-machine", label: "Add Machine", icon: <Cpu size={18} /> },
    { id: "add-tool", label: "Add Tool", icon: <Wrench size={18} /> },
    { id: "add-accessories", label: "Add Accessories", icon: <Package size={18} /> },
    { id: "add-process-cost", label: "Add Process Cost", icon: <DollarSign size={18} /> },
    { id: "process-list", label: "Process List", icon: <List size={18} /> },
    { id: "approved-root-sheet", label: "Approved Root Sheet", icon: <FileCheck size={18} /> },
    { id: "last-process-products", label: "Last Process Products", icon: <Layers size={18} /> }
  ];

  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleConfirmLogout = () => {
    setShowLogoutModal(false);
    onLogout();
    navigate('/login');
  };
  const handleCancelLogout = () => setShowLogoutModal(false);

  const getPageTitle = () => {
    switch (activeTab) {
      case "list-schedule": return "List Schedule";
      case "list-approved-sale-order": return "List Approved Sale Order";
      case "create-production-plan": return "Create Production Plan";
      case "list-employee": return "List Employee";
      case "approved-bom": return "Approved BOM";
      case "add-machine": return "Add Machine";
      case "add-tool": return "Add Tool";
      case "add-accessories": return "Add Accessories";
      case "add-process-cost": return "Add Process Cost";
      case "process-list": return "Process List";
      case "approved-root-sheet": return "Approved Root Sheet";
      case "last-process-products": return "Last Process Products";
      default: return "Supervisor Dashboard";
    }
  };

  // Handle Edit Sale Order
  const handleEditSaleOrder = (order) => {
    alert(`Edit Sale Order ${order.orderNumber}`);
  };

  // Handle Submit Sale Order
  const handleSubmitSaleOrder = (order) => {
    alert(`Sale Order ${order.orderNumber} submitted for production!`);
  };

  // Handle Production Plan - Stock Availability
  const handleStockAvailability = (plan) => {
    alert(`Stock Available: ${plan.stockAvailable}\nRequired: ${plan.quantity}\nShortage: ${plan.quantity - plan.stockAvailable}`);
  };

  // Handle Last Process Selection
  const handleLastProcess = () => {
    alert("Last Process - Select product from dropdown");
  };

  // Handle Outsource to Vendor
  const handleOutsourceToVendor = () => {
    alert("Outsource to Vendor - Send production request to external vendor");
  };

  // Handle Inhouse Production
  const handleInhouseProduction = () => {
    setShowInhouseModal(true);
  };

  // Handle Receive Material
  const handleReceiveMaterial = (plan) => {
    setSelectedProductionPlan(plan);
    setShowReceiveMaterialModal(true);
  };

  const handleSubmitReceiveMaterial = () => {
    alert(`Material received for production plan!\nProduct: ${receiveMaterialForm.product}\nDate: ${receiveMaterialForm.date}\nTime: ${receiveMaterialForm.fromTime} to ${receiveMaterialForm.toTime}`);
    setShowReceiveMaterialModal(false);
    setReceiveMaterialForm({ product: "", date: "", fromTime: "", toTime: "", fromDate: "", toDate: "" });
  };

  // Handle Add Process in Inhouse Production
  const handleAddProcess = () => {
    setProcesses([...processes, { id: Date.now(), ...inhouseForm }]);
    alert("Process added successfully!");
  };

  const handleSubmitInhouseProduction = () => {
    alert("Inhouse Production plan submitted successfully!");
    setShowInhouseModal(false);
    setProcesses([]);
    setInhouseForm({
      plannedProduct: "",
      employee: "",
      machine: "",
      tool: "",
      instrument: "",
      selectedAccessories: [],
      checklist: { noDamage: false, noBent: false },
      data: { weightLoss: "", accepted: "", missedQuantity: "" }
    });
  };

  // Filter function
  const filteredData = (data, searchFields) => {
    if (!searchTerm) return data;
    return data.filter(item => 
      searchFields.some(field => 
        item[field]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  // List Schedule Component
  const ListScheduleContent = () => (
    <div className="content-container">
      <div className="filter-bar">
        <Search size={18} />
        <input type="text" placeholder="Search schedules..." className="search-input" 
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
      </div>
      <div className="data-table-container">
        <table className="data-table">
          <thead><tr><th>Schedule ID</th><th>Customer</th><th>Product</th><th>Quantity</th><th>Date</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {filteredData(schedules, ['id', 'customer', 'product']).map(sch => (
              <tr key={sch.id}>
                <td className="highlight">{sch.id}</td>
                <td>{sch.customer}</td>
                <td>{sch.product}</td>
                <td>{sch.quantity}</td>
                <td>{sch.date}</td>
                <td><span className="status approved">{sch.status}</span></td>
                <td><button className="action-btn view" onClick={() => alert(`Schedule Details: ${sch.id}`)}><Eye size={16} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // List Approved Sale Order Component
  const ListApprovedSaleOrderContent = () => (
    <div className="content-container">
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr><th>Order #</th><th>Customer</th><th>Product Details</th><th>Quantity</th><th>Amount</th><th>Date</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {approvedSaleOrders.map(order => (
              <tr key={order.id}>
                <td className="highlight">{order.orderNumber}</td>
                <td>{order.customer}</td>
                <td>{order.product}</td>
                <td>{order.quantity}</td>
                <td className="amount">{order.amount}</td>
                <td>{order.date}</td>
                <td><span className="status approved">{order.status}</span></td>
                <td className="action-buttons">
                  <button className="action-btn edit" onClick={() => handleEditSaleOrder(order)}><Edit size={16} /> Edit</button>
                  <button className="action-btn approve" onClick={() => handleSubmitSaleOrder(order)}><Check size={16} /> Submit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Create Production Plan Component
  const CreateProductionPlanContent = () => (
    <div className="content-container">
      {productionPlans.map(plan => (
        <div key={plan.id} className="production-plan-card">
          <div className="plan-header">
            <h3>Production Plan - {plan.product}</h3>
            <span className="status pending">{plan.status}</span>
          </div>
          <div className="plan-details">
            <p><strong>Quantity:</strong> {plan.quantity}</p>
            <p><strong>Stock Available:</strong> {plan.stockAvailable}</p>
            <p><strong>Required:</strong> {plan.required}</p>
            <p><strong>Sub Products:</strong> {plan.subProducts.join(', ')}</p>
          </div>
          <div className="plan-actions">
            <button className="action-btn view" onClick={() => handleStockAvailability(plan)}><Package size={16} /> Stock Availability</button>
            <button className="action-btn" style={{background: "#FEF3C7", color: "#D97706"}} onClick={handleLastProcess}><Layers size={16} /> 1. Last Process</button>
            <button className="action-btn" style={{background: "#E0E7FF", color: "#4F46E5"}} onClick={handleOutsourceToVendor}><Truck size={16} /> 2. Outsource to vendor</button>
            <button className="action-btn approve" onClick={handleInhouseProduction}><Factory size={16} /> 3. Inhouse Production</button>
            <button className="action-btn edit" onClick={() => handleReceiveMaterial(plan)}><Package size={16} /> Receive Material</button>
          </div>
        </div>
      ))}

      {/* Receive Material Modal */}
      {showReceiveMaterialModal && selectedProductionPlan && (
        <div className="modal-overlay" onClick={() => setShowReceiveMaterialModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>Receive Material - {selectedProductionPlan.product}</h2><button className="modal-close" onClick={() => setShowReceiveMaterialModal(false)}>×</button></div>
            <div className="modal-body">
              <div className="form-group"><label>Product:</label><input type="text" className="form-input" value={selectedProductionPlan.product} readOnly /></div>
              <div className="form-group"><label>Date:</label><input type="date" className="form-input" value={receiveMaterialForm.date} onChange={(e) => setReceiveMaterialForm({...receiveMaterialForm, date: e.target.value})} /></div>
              <div className="form-row"><div className="form-group"><label>From Time:</label><input type="time" className="form-input" value={receiveMaterialForm.fromTime} onChange={(e) => setReceiveMaterialForm({...receiveMaterialForm, fromTime: e.target.value})} /></div>
              <div className="form-group"><label>To Time:</label><input type="time" className="form-input" value={receiveMaterialForm.toTime} onChange={(e) => setReceiveMaterialForm({...receiveMaterialForm, toTime: e.target.value})} /></div></div>
              <div className="form-row"><div className="form-group"><label>From Date:</label><input type="date" className="form-input" value={receiveMaterialForm.fromDate} onChange={(e) => setReceiveMaterialForm({...receiveMaterialForm, fromDate: e.target.value})} /></div>
              <div className="form-group"><label>To Date:</label><input type="date" className="form-input" value={receiveMaterialForm.toDate} onChange={(e) => setReceiveMaterialForm({...receiveMaterialForm, toDate: e.target.value})} /></div></div>
            </div>
            <div className="modal-footer"><button className="btn-approve" onClick={handleSubmitReceiveMaterial}>Submit</button><button className="btn-cancel" onClick={() => setShowReceiveMaterialModal(false)}>Cancel</button></div>
          </div>
        </div>
      )}

      {/* Inhouse Production Modal */}
      {showInhouseModal && (
        <div className="modal-overlay" onClick={() => setShowInhouseModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>Inhouse Production</h2><button className="modal-close" onClick={() => setShowInhouseModal(false)}>×</button></div>
            <div className="modal-body">
              <div className="form-group"><label>Planned Product:</label><select className="form-input" value={inhouseForm.plannedProduct} onChange={(e) => setInhouseForm({...inhouseForm, plannedProduct: e.target.value})}><option value="">Select Product</option><option>Product A</option><option>Product B</option></select></div>
              <div className="form-section"><h4>Add/Edit/Delete Process</h4>
                <div className="form-grid">
                  <select className="form-input" placeholder="Select Employee" value={inhouseForm.employee} onChange={(e) => setInhouseForm({...inhouseForm, employee: e.target.value})}><option value="">Select Employee</option>{employees.map(e => <option key={e.id}>{e.name}</option>)}</select>
                  <select className="form-input" placeholder="Select Machine" value={inhouseForm.machine} onChange={(e) => setInhouseForm({...inhouseForm, machine: e.target.value})}><option value="">Select Machine</option>{machines.map(m => <option key={m.id}>{m.name}</option>)}</select>
                  <select className="form-input" placeholder="Select Tool" value={inhouseForm.tool} onChange={(e) => setInhouseForm({...inhouseForm, tool: e.target.value})}><option value="">Select Tool</option>{tools.map(t => <option key={t.id}>{t.name}</option>)}</select>
                  <select className="form-input" placeholder="Select Instrument" value={inhouseForm.instrument} onChange={(e) => setInhouseForm({...inhouseForm, instrument: e.target.value})}><option value="">Select Instrument</option><option>Caliper</option><option>Gauge</option></select>
                </div>
                <div className="checkbox-group"><label><input type="checkbox" onChange={(e) => {
                  if (e.target.checked) setInhouseForm({...inhouseForm, selectedAccessories: [...inhouseForm.selectedAccessories, e.target.value]});
                  else setInhouseForm({...inhouseForm, selectedAccessories: inhouseForm.selectedAccessories.filter(a => a !== e.target.value)});
                }} value="Safety Gloves" /> Safety Gloves</label><label><input type="checkbox" value="Goggles" onChange={(e) => {
                  if (e.target.checked) setInhouseForm({...inhouseForm, selectedAccessories: [...inhouseForm.selectedAccessories, e.target.value]});
                  else setInhouseForm({...inhouseForm, selectedAccessories: inhouseForm.selectedAccessories.filter(a => a !== e.target.value)});
                }} /> Goggles</label></div>
              </div>
              <div className="form-section"><h4>Checklist & Data</h4>
                <div className="checklist-row"><label><input type="checkbox" checked={inhouseForm.checklist.noDamage} onChange={(e) => setInhouseForm({...inhouseForm, checklist: {...inhouseForm.checklist, noDamage: e.target.checked}})} /> No Damage</label>
                <label><input type="checkbox" checked={inhouseForm.checklist.noBent} onChange={(e) => setInhouseForm({...inhouseForm, checklist: {...inhouseForm.checklist, noBent: e.target.checked}})} /> No Bent</label></div>
                <div className="form-row"><input type="text" className="form-input" placeholder="Weight Loss" value={inhouseForm.data.weightLoss} onChange={(e) => setInhouseForm({...inhouseForm, data: {...inhouseForm.data, weightLoss: e.target.value}})} />
                <input type="text" className="form-input" placeholder="Accepted" value={inhouseForm.data.accepted} onChange={(e) => setInhouseForm({...inhouseForm, data: {...inhouseForm.data, accepted: e.target.value}})} />
                <input type="text" className="form-input" placeholder="Missed Quantity" value={inhouseForm.data.missedQuantity} onChange={(e) => setInhouseForm({...inhouseForm, data: {...inhouseForm.data, missedQuantity: e.target.value}})} /></div>
              </div>
              <button className="btn-secondary" onClick={handleAddProcess}><Plus size={16} /> Add</button>
              {processes.length > 0 && (<div className="processes-list"><h4>Added Processes</h4>{processes.map(p => (<div key={p.id} className="process-item">{p.plannedProduct} - {p.employee} <button className="delete-btn" onClick={() => setProcesses(processes.filter(proc => proc.id !== p.id))}><Trash2 size={14} /></button></div>))}</div>)}
            </div>
            <div className="modal-footer"><button className="btn-approve" onClick={handleSubmitInhouseProduction}>Submit</button><button className="btn-cancel" onClick={() => setShowInhouseModal(false)}>Cancel</button></div>
          </div>
        </div>
      )}
    </div>
  );

  // List Employee Component
  const ListEmployeeContent = () => (
    <div className="content-container">
      <div className="data-table-container">
        <table className="data-table">
          <thead><tr><th>Employee Code</th><th>Name</th><th>Designation</th><th>Department</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>{employees.map(emp => (<tr key={emp.id}><td className="highlight">{emp.id}</td><td>{emp.name}</td><td>{emp.designation}</td><td>{emp.department}</td><td><span className="status approved">{emp.status}</span></td><td><button className="action-btn view"><Eye size={16} /></button></td></tr>))}</tbody>
        </table>
      </div>
    </div>
  );

  // Approved BOM Component
  const ApprovedBOMContent = () => (
    <div className="content-container">
      {approvedBOM.map(bom => (<div key={bom.id} className="bom-card"><h3>{bom.product}</h3><div className="bom-components"><h4>Components:</h4><ul>{bom.components.map((comp, idx) => <li key={idx}>{comp}</li>)}</ul></div></div>))}
    </div>
  );

  // Add Machine Component
  const AddMachineContent = () => (
    <div className="content-container">
      <form className="add-form" onSubmit={(e) => { e.preventDefault(); alert("Machine added!"); }}>
        <div className="form-grid"><div className="form-group"><label>Machine Name:</label><input type="text" className="form-input" required /></div>
        <div className="form-group"><label>Machine Type:</label><select className="form-input"><option>Industrial</option><option>Automated</option></select></div>
        <div className="form-group"><label>Status:</label><select className="form-input"><option>Active</option><option>Inactive</option></select></div></div>
        <button type="submit" className="btn-submit">Add Machine</button>
      </form>
      <div className="data-table-container"><table className="data-table"><thead><tr><th>Machine Name</th><th>Type</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>{machines.map(m => (<tr key={m.id}><td>{m.name}</td><td>{m.type}</td><td><span className="status approved">{m.status}</span></td><td><button className="action-btn edit"><Edit size={16} /></button></td></tr>))}</tbody></table></div>
    </div>
  );

  // Add Tool Component
  const AddToolContent = () => (
    <div className="content-container">
      <form className="add-form" onSubmit={(e) => { e.preventDefault(); alert("Tool added!"); }}>
        <div className="form-grid"><div className="form-group"><label>Tool Name:</label><input type="text" className="form-input" required /></div>
        <div className="form-group"><label>Tool Type:</label><input type="text" className="form-input" /></div>
        <div className="form-group"><label>Status:</label><select className="form-input"><option>Active</option><option>Inactive</option></select></div></div>
        <button type="submit" className="btn-submit">Add Tool</button>
      </form>
      <div className="data-table-container"><table className="data-table"><thead><tr><th>Tool Name</th><th>Type</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>{tools.map(t => (<tr key={t.id}><td>{t.name}</td><td>{t.type}</td><td><span className="status approved">{t.status}</span></td><td><button className="action-btn edit"><Edit size={16} /></button></td></tr>))}</tbody></table></div>
    </div>
  );

  // Add Accessories Component
  const AddAccessoriesContent = () => (
    <div className="content-container">
      <form className="add-form" onSubmit={(e) => { e.preventDefault(); alert("Accessory added!"); }}>
        <div className="form-grid"><div className="form-group"><label>Accessory Name:</label><input type="text" className="form-input" required /></div>
        <div className="form-group"><label>Type:</label><input type="text" className="form-input" /></div>
        <div className="form-group"><label>Status:</label><select className="form-input"><option>Active</option><option>Inactive</option></select></div></div>
        <button type="submit" className="btn-submit">Add Accessory</button>
      </form>
      <div className="data-table-container"><table className="data-table"><thead><tr><th>Accessory Name</th><th>Type</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>{accessories.map(a => (<tr key={a.id}><td>{a.name}</td><td>{a.type}</td><td><span className="status approved">{a.status}</span></td><td><button className="action-btn edit"><Edit size={16} /></button></td></tr>))}</tbody></table></div>
    </div>
  );

  // Add Process Cost Component
  const AddProcessCostContent = () => (
    <div className="content-container">
      <form className="add-form" onSubmit={(e) => { e.preventDefault(); alert("Process cost added!"); }}>
        <div className="form-grid"><div className="form-group"><label>Process Name:</label><input type="text" className="form-input" required /></div>
        <div className="form-group"><label>Machine Cost:</label><input type="text" className="form-input" /></div>
        <div className="form-group"><label>Labor Cost:</label><input type="text" className="form-input" /></div></div>
        <button type="submit" className="btn-submit">Add Process Cost</button>
      </form>
      <div className="data-table-container"><table className="data-table"><thead><tr><th>Process Name</th><th>Machine Cost</th><th>Labor Cost</th><th>Total</th><th>Actions</th></tr></thead>
        <tbody>{processCosts.map(p => (<tr key={p.id}><td>{p.processName}</td><td>{p.machine}</td><td>{p.labor}</td><td className="amount">{p.total}</td><td><button className="action-btn edit"><Edit size={16} /></button></td></tr>))}</tbody></table></div>
    </div>
  );

  // Process List Component
  const ProcessListContent = () => (
    <div className="content-container">
      <div className="data-table-container"><table className="data-table"><thead><tr><th>Process Name</th><th>Product</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>{processList.map(p => (<tr key={p.id}><td className="highlight">{p.name}</td><td>{p.product}</td><td><span className="status approved">{p.status}</span></td><td><button className="action-btn view"><Eye size={16} /></button></td></tr>))}</tbody></table></div>
    </div>
  );

  // Approved Root Sheet Component
  const ApprovedRootSheetContent = () => (
    <div className="content-container">
      <div className="root-sheet-list">
        {approvedRootSheets.map(sheet => (
          <div key={sheet.id} className="root-sheet-card" onClick={() => alert(`Root Sheet Details:\nProduct: ${sheet.product}\nProcesses: ${sheet.processes.join(' → ')}`)}>
            <h3>Production Route Sheet {sheet.id}</h3>
            <p><strong>Product:</strong> {sheet.product}</p>
            <p><strong>Processes:</strong> {sheet.processes.join(' → ')}</p>
            <span className="status approved">{sheet.status}</span>
          </div>
        ))}
      </div>
    </div>
  );

  // Last Process Products Component
  const LastProcessProductsContent = () => (
    <div className="content-container">
      <div className="data-table-container"><table className="data-table"><thead><tr><th>Product</th><th>Last Process</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>{lastProcessProducts.map(p => (<tr key={p.id}><td className="highlight">{p.product}</td><td>{p.lastProcess}</td><td><span className={`status ${p.status === 'Completed' ? 'approved' : 'pending'}`}>{p.status}</span></td><td><button className="action-btn view"><Eye size={16} /></button></td></tr>))}</tbody></table></div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "list-schedule": return <ListScheduleContent />;
      case "list-approved-sale-order": return <ListApprovedSaleOrderContent />;
      case "create-production-plan": return <CreateProductionPlanContent />;
      case "list-employee": return <ListEmployeeContent />;
      case "approved-bom": return <ApprovedBOMContent />;
      case "add-machine": return <AddMachineContent />;
      case "add-tool": return <AddToolContent />;
      case "add-accessories": return <AddAccessoriesContent />;
      case "add-process-cost": return <AddProcessCostContent />;
      case "process-list": return <ProcessListContent />;
      case "approved-root-sheet": return <ApprovedRootSheetContent />;
      case "last-process-products": return <LastProcessProductsContent />;
      default: return <div className="content-container"><h2>{getPageTitle()}</h2></div>;
    }
  };

  return (
    <div className="supervisor-dashboard-container">
      <div className="supervisor-sidebar">
        <div className="supervisor-logo-container">
          <div className="supervisor-logo-icon"><img src="/rahulerp.jpg" alt="Company Logo" className="supervisor-logo-image" /></div>
          <div><h2 className="supervisor-company-name">Ragul Auto Tech</h2><p className="supervisor-company-tagline">AI Powered ERP</p></div>
        </div>
        <nav className="supervisor-nav-menu">
          {tabItems.map((tab) => (<button key={tab.id} className={`supervisor-nav-item ${activeTab === tab.id ? 'supervisor-nav-item-active' : ''}`} onClick={() => setActiveTab(tab.id)}><span className="supervisor-nav-icon">{tab.icon}</span><span>{tab.label}</span></button>))}
        </nav>
        <div className="supervisor-logout-container"><button onClick={handleLogoutClick} className="supervisor-logout-button"><LogOut size={18} /> Logout</button></div>
      </div>

      <div className="supervisor-main-content">
        <header className="supervisor-header">
          <div><h1 className="supervisor-main-title">Supervisor Dashboard</h1><div className="supervisor-subtitle">Welcome, {user?.role || 'Production Supervisor'}</div></div>
          <div className="supervisor-header-actions"><div className="supervisor-last-updated">Last updated: {new Date().toLocaleString()}</div></div>
        </header>
        <BackButton />
        <div className="supervisor-content-card">{renderContent()}</div>
        {showLogoutModal && (<div className="logout-modal-overlay"><div className="logout-modal"><div className="logout-modal-header"><div className="logout-modal-icon">👋</div><h3>Confirm Logout</h3></div><div className="logout-modal-body"><p>Are you sure you want to logout from <strong>Supervisor</strong> dashboard?</p><p className="logout-modal-warning">You will be redirected to the login page.</p></div><div className="logout-modal-footer"><button className="logout-modal-cancel" onClick={handleCancelLogout}>Cancel</button><button className="logout-modal-confirm" onClick={handleConfirmLogout}>Yes, Logout</button></div></div></div>)}
      </div>
    </div>
  );
}